import BaseSliderBlock from "./BaseSliderBlock";
import Waypoint from "../../Utils/Waypoint";
import Main from "@/ts/base/Main";
import Swiper, {Autoplay, Controller, Navigation} from "swiper";
import debounce from "debounce";

export default class Hero extends BaseSliderBlock {

	public hasStickyContent = true;

	protected formHolderEl!: HTMLElement|null;
	protected formColEl!: HTMLElement|null;
	protected backgroundEl!: HTMLElement|null;
	protected contentEl!: HTMLElement|null;


	init(): void {
		this.formHolderEl = this.block.querySelector(".form-wrapper");
		this.backgroundEl = this.block.querySelector(".section-background");
		this.contentEl = this.block.querySelector(".section-content");


		if (this.backgroundEl !== null && this.block.classList.contains("blur-effect-enabled")) {
			window.addEventListener("scroll", debounce(this.onWindowScroll.bind(this), 10, true), {
				passive: true
			});
		}

		if (this.contentEl && this.block.classList.contains("variant-form")) {
			const textEl = this.block.querySelector(".text");
			if (textEl instanceof HTMLElement) {
				const posTextEl = textEl.getBoundingClientRect();
				textEl.style.top = posTextEl.top + "px";
			}
		}

		this.initSliders();
		this.initFormVariant();
		this.initStickyContent();
	}

	protected initSliders(): void {
		const backgroundSliderEl = this.block.querySelector(".section-background .swiper");
		const contentSliderEl = this.block.querySelector(".section-content .swiper");

		if ((backgroundSliderEl instanceof HTMLElement) && (contentSliderEl instanceof HTMLElement)) {
			const backgroundSlider = new Swiper(backgroundSliderEl, {
				loop: true,
			});

			const contentSlider = new Swiper(contentSliderEl, {
				loop: true,
				spaceBetween: 40,
				speed: parseInt(String(contentSliderEl.dataset.swiperSpeed)),
				navigation: this.getDefaultNavigation(),
				pagination: this.getDefaultPagination(),
				autoplay: {
					delay: parseInt(String(contentSliderEl.dataset.swiperDelay)),
					disableOnInteraction: false,
					pauseOnMouseEnter: true
				},
				modules: [Autoplay, Navigation, Controller],
				controller: {
					control: backgroundSlider
				}
			});
		}
	}

	protected initStickyContent(): void {
		if (this.contentEl && this.hasStickyContent) {
			[Main.IsLoadedClass, "resize"].forEach(type => {
				window.addEventListener(type, () => {
					this.adjustStickyContentPosition();
				});
			});
		}
	}

	protected initFormVariant(): void {

		if (this.formHolderEl && this.formHolderEl.classList.contains("fixed")) {
			this.formColEl = this.formHolderEl.parentElement;

			[Main.IsLoadedClass, "resize", "scroll"].forEach(type => {
				window.addEventListener(type, () => {
					this.adjustFormPositionAndSize();
				}, {
					passive: true
				});
			});
		}
		else if (this.formHolderEl) {
			[Main.IsLoadedClass, "resize"].forEach(type => {
				window.addEventListener(type, () => {
					this.adjustPadding();
				});
			});
		}

	}

	blurBackground(): void {
		if (!(this.backgroundEl instanceof HTMLElement) || !this.block.classList.contains(Waypoint.InViewClass)) {
			return;
		}

		const backgroundImageEl = this.backgroundEl.querySelector('.background-image');

		if (!(backgroundImageEl instanceof HTMLElement)) {
			return;
		}

		const scroll = window.scrollY;
		const scrollPers = scroll / document.body.scrollHeight / 2;

		if (scroll > 0) {
			backgroundImageEl.style.filter = `blur(${50 * scrollPers}px)`;
			backgroundImageEl.style.transform = `scale(${1 + scrollPers})`;
		}
		else {
			backgroundImageEl.style.filter = "";
			backgroundImageEl.style.transform = "";
		}
	}

	adjustFormPositionAndSize(): void {
		if (!(this.formHolderEl instanceof HTMLElement) || !(this.formColEl instanceof HTMLElement)) {
			return;
		}

		const computed = window.getComputedStyle(this.formHolderEl);
		if (computed.position === "fixed") {
			const header = Main.Instance.getHeaderComponent();
			const formHeight = this.formHolderEl.clientHeight;
			const headerHeight = (header && !header?.isFixedTransparent()) ? header?.getHeight() : 0;
			const contentHeight = document.getElementById("SiteContent")?.clientHeight || 0;
			const scroll = window.scrollY;
			const limit = headerHeight + contentHeight - formHeight;
			let top = this.formColEl.getBoundingClientRect().top;
			if (scroll > limit) {
				top = -(scroll - limit);
			}
			else if (scroll > top) {
				top = headerHeight;
			}

			this.formHolderEl.style.top = (top).toString().concat("px");
			this.formHolderEl.style.width = (this.formColEl.clientWidth).toString().concat("px");
		}
		else {
			this.formHolderEl.style.top = "";
			this.formHolderEl.style.width = "";
		}
	}

	adjustStickyContentPosition(): void {
		if (!(this.contentEl instanceof HTMLElement)) {
			return;
		}

		const sectionContentCompStyle = window.getComputedStyle(this.contentEl);
		if (sectionContentCompStyle.position !== "sticky") {
			return;
		}

		const header = Main.Instance.getHeaderComponent();
		const headerHeight = header ? header.getHeight() : 0;
		this.block.style.height = "";
		this.block.style.top = "";
		this.contentEl.removeAttribute("style");
		if (document.body.classList.contains("desktop")) {
			window.setTimeout(() => {
				if (this.block.classList.contains("has-background") && (this.contentEl instanceof HTMLElement)) {
					if (this.block.classList.contains('fullscreen')) {
						this.contentEl.style.top = headerHeight + (this.block.clientHeight / 2) - (this.contentEl.clientHeight / 2) + "px";
					}
					else if (!this.block.classList.contains("has-background-video")) {
						let space = 40;
						if (window.matchMedia('(min-width:768px)').matches) {
							space = 80;
						}
						let hContent = this.contentEl.clientHeight;
						if (!this.maintainAspectRatio) {
							this.block.style.height = hContent + space * 2 + "px";
						}
						else if (this.fixedAspectRatioImg) {
							this.block.style.height = Math.round(this.fixedAspectRatioImg.getRealHeight()).toString().concat("px");
						}

						this.contentEl.style.top = headerHeight + space + "px";
					}
				}
			}, 300);
		}
	}

	adjustPadding(): void {
		if (this.block.classList.contains("has-background")) {
			const header = Main.Instance.getHeaderComponent();
			if (header && header.isFixedTransparent()) {
				this.block.style.paddingTop = "";
				const paddingTop = parseInt(this.getComputedStyle().paddingTop);
				this.block.style.paddingTop = (paddingTop + header.getHeight()).toString().concat("px");
			}
		}
	}

	updateSize() {
		// do nothing
	}

	protected onWindowScroll(e: Event) {
		this.blurBackground();
	}
}
